import React, { useCallback, useMemo, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { blue, red } from '@mui/material/colors';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import Table from '~/components/v2/common/Table/Table';

import * as AccommodationService from '~/services/AccommodationService';
import { Icon } from '~/services/AccommodationService';

import { InlineNumberEditCell, InlineTextFieldEditCell } from '../../../utils/datagrid';

interface ManageCatalogGroupsModalProps {
  open: boolean;
  onClose: () => void;
  type: 'facility' | 'amenity'; // Type filter (e.g., 'facility', 'amenity')
}

interface CatalogGroupRowData extends AccommodationService.AdminCatalogGroup {
  isNew?: boolean;
}

interface PendingChanges {
  name?: string;
  description?: string | null;
  displayOrder?: number;
  icon?: Icon | null;
  // For new rows, we also need code and type
  code?: string;
  type?: string;
}

const NEW_ROW_ID_PREFIX = 'new-';

// Helper function to get the group label based on type
function getGroupLabel(type?: string, plural = false): string {
  if (!type) {
    return plural ? 'Catalog Groups' : 'Catalog Group';
  }
  const capitalizedType = type.charAt(0).toUpperCase() + type.slice(1);
  return plural ? `${capitalizedType} Groups` : `${capitalizedType} Group`;
}

export default function ManageCatalogGroupsModal({ open, onClose, type }: ManageCatalogGroupsModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [modifiedRows, setModifiedRows] = useState<Record<string, PendingChanges>>({});
  const [localRows, setLocalRows] = useState<Array<CatalogGroupRowData>>([]);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  // Fetch catalog groups
  const {
    data: catalogGroupsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['accommodation', 'catalog-groups', 'all'],
    queryFn: async () => {
      const response = await AccommodationService.listCatalogGroups({ limit: '1000', page: '1' });
      return response.result;
    },
    enabled: open,
  });

  // Fetch icons for dropdown
  const { data: iconsData } = useQuery({
    queryKey: ['accommodation', 'icons'],
    queryFn: async () => {
      const response = await AccommodationService.listIcons();
      return response.result;
    },
    enabled: open,
  });

  const icons = iconsData?.icons ?? [];

  // Combine API data with local new rows (local rows first to appear at top)
  const allRows = useMemo(() => {
    const apiGroups = catalogGroupsData?.groups ?? [];
    const combinedRows = [...localRows, ...apiGroups];

    // Filter by type
    return combinedRows.filter((row) => row.type === type);
  }, [catalogGroupsData?.groups, localRows, type]);

  // Create catalog group mutation
  const createMutation = useMutation({
    mutationFn: async (data: AccommodationService.CatalogGroupCreatePayload & { tempId: string }) => {
      const { tempId, ...payload } = data;
      return await AccommodationService.createCatalogGroup(payload);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['accommodation', 'catalog-groups'] });
      // Remove the temporary local row after successful creation
      setLocalRows((prev) => prev.filter((r) => r.id !== variables.tempId));
      setModifiedRows((prev) => {
        const updated = { ...prev };
        delete updated[variables.tempId];
        return updated;
      });
      enqueueSnackbar(`${getGroupLabel(type)} created successfully`, { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error creating ${getGroupLabel(type).toLowerCase()}: ${error.message}`, { variant: 'error' });
    },
  });

  // Update catalog group mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: AccommodationService.CatalogGroupUpdatePayload }) => {
      return await AccommodationService.updateCatalogGroup({ id }, data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['accommodation', 'catalog-groups'] });
      setModifiedRows((prev) => {
        const updated = { ...prev };
        delete updated[variables.id];
        return updated;
      });
      enqueueSnackbar(`${getGroupLabel(type)} updated successfully`, { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error updating ${getGroupLabel(type).toLowerCase()}: ${error.message}`, { variant: 'error' });
    },
  });

  // Delete catalog group mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return await AccommodationService.deleteCatalogGroup({ id });
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['accommodation', 'catalog-groups'] });
      setDeletingIds((prev) => {
        const updated = new Set(prev);
        updated.delete(id);
        return updated;
      });
      enqueueSnackbar(`${getGroupLabel(type)} deleted successfully`, { variant: 'success' });
    },
    onError: (error: Error, id) => {
      setDeletingIds((prev) => {
        const updated = new Set(prev);
        updated.delete(id);
        return updated;
      });
      enqueueSnackbar(`Error deleting ${getGroupLabel(type).toLowerCase()}: ${error.message}`, { variant: 'error' });
    },
  });

  const handleAddNew = useCallback(() => {
    const defaultType = type ?? 'facility';
    const tempId = `${NEW_ROW_ID_PREFIX}${Date.now()}`;
    const newRow: CatalogGroupRowData = {
      id: tempId,
      code: '',
      type: defaultType,
      name: '',
      description: null,
      displayOrder: undefined,
      icon: null,
      isNew: true,
    };
    // Prepend new row so it appears at the top
    setLocalRows((prev) => [newRow, ...prev]);
    setModifiedRows((prev) => ({
      ...prev,
      [tempId]: { code: '', type: defaultType, name: '' },
    }));
  }, [type]);

  const handleSave = useCallback(
    (row: CatalogGroupRowData) => {
      const changes = modifiedRows[row.id];
      if (!changes) return;

      if (row.isNew || row.id.startsWith(NEW_ROW_ID_PREFIX)) {
        // Create new group
        if (!changes.code || !changes.type || !changes.name) {
          enqueueSnackbar(`Code, Type, and Name are required for new ${getGroupLabel(type, true).toLowerCase()}`, {
            variant: 'error',
          });
          return;
        }
        createMutation.mutate({
          tempId: row.id,
          code: changes.code,
          type: changes.type,
          name: changes.name,
          description: changes.description,
          displayOrder: changes.displayOrder,
          icon: changes.icon as AccommodationService.CatalogGroupIcon,
        });
      } else {
        // Update existing group
        updateMutation.mutate({
          id: row.id,
          data: {
            name: changes.name,
            description: changes.description,
            displayOrder: changes.displayOrder,
            icon: changes.icon as AccommodationService.CatalogGroupIcon,
          },
        });
      }
    },
    [modifiedRows, createMutation, updateMutation, enqueueSnackbar],
  );

  const handleDelete = useCallback(
    (row: CatalogGroupRowData) => {
      if (row.isNew || row.id.startsWith(NEW_ROW_ID_PREFIX)) {
        // Just remove local row
        setLocalRows((prev) => prev.filter((r) => r.id !== row.id));
        setModifiedRows((prev) => {
          const updated = { ...prev };
          delete updated[row.id];
          return updated;
        });
      } else {
        // Delete from API
        setDeletingIds((prev) => new Set(prev).add(row.id));
        deleteMutation.mutate(row.id);
      }
    },
    [deleteMutation],
  );

  const handleClearChanges = useCallback((id: string) => {
    const isNewRow = id.startsWith(NEW_ROW_ID_PREFIX);

    if (isNewRow) {
      // For new rows, remove them entirely
      setLocalRows((prev) => prev.filter((r) => r.id !== id));
    }
    // Clear the tracked changes - inline controls will re-render with original values
    setModifiedRows((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  }, []);

  const columns: Array<GridColDef<CatalogGroupRowData>> = useMemo(
    () => [
      {
        field: 'code',
        headerName: 'Code',
        width: 150,
        flex: 0,
        renderCell: (params: GridRenderCellParams<CatalogGroupRowData>) => {
          const isNewRow = params.row.id.startsWith(NEW_ROW_ID_PREFIX);
          if (!isNewRow) {
            return <Typography variant="body2">{params.value ?? '-'}</Typography>;
          }
          return (
            <InlineTextFieldEditCell
              value={modifiedRows[params.row.id]?.code ?? ''}
              onChange={(value) => {
                setModifiedRows((prev) => ({
                  ...prev,
                  [params.row.id]: { ...prev[params.row.id], code: value },
                }));
              }}
              placeholder="Enter code"
            />
          );
        },
      },
      {
        field: 'name',
        headerName: 'Name',
        width: 200,
        flex: 0,
        renderCell: (params: GridRenderCellParams<CatalogGroupRowData>) => {
          const currentValue = modifiedRows[params.row.id]?.name ?? params.value;
          return (
            <InlineTextFieldEditCell
              value={currentValue}
              onChange={(value) => {
                setModifiedRows((prev) => ({
                  ...prev,
                  [params.row.id]: { ...prev[params.row.id], name: value },
                }));
              }}
            />
          );
        },
      },
      {
        field: 'description',
        headerName: 'Description',
        width: 250,
        flex: 0,
        renderCell: (params: GridRenderCellParams<CatalogGroupRowData>) => {
          const currentValue = modifiedRows[params.row.id]?.description ?? params.value;
          return (
            <InlineTextFieldEditCell
              value={currentValue}
              onChange={(value) => {
                setModifiedRows((prev) => ({
                  ...prev,
                  [params.row.id]: { ...prev[params.row.id], description: value || null },
                }));
              }}
            />
          );
        },
      },
      {
        field: 'displayOrder',
        headerName: 'Rank',
        width: 120,
        flex: 0,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params: GridRenderCellParams<CatalogGroupRowData>) => {
          const currentValue = modifiedRows[params.row.id]?.displayOrder ?? params.value;
          return (
            <InlineNumberEditCell
              value={currentValue}
              onChange={(value) => {
                setModifiedRows((prev) => ({
                  ...prev,
                  [params.row.id]: { ...prev[params.row.id], displayOrder: value },
                }));
              }}
            />
          );
        },
      },
      {
        field: 'icon',
        headerName: 'Icon',
        width: 200,
        flex: 0,
        renderCell: (params: GridRenderCellParams<CatalogGroupRowData>) => {
          const pendingIcon = modifiedRows[params.row.id]?.icon;
          const currentIcon = pendingIcon !== undefined ? pendingIcon : params.value;

          return (
            <Autocomplete
              size="small"
              options={icons}
              value={currentIcon ?? null}
              onChange={(_event, newValue: Icon | null) => {
                setModifiedRows((prev) => ({
                  ...prev,
                  [params.row.id]: { ...prev[params.row.id], icon: newValue },
                }));
              }}
              getOptionLabel={(option) => option}
              isOptionEqualToValue={(option, value) => option === value}
              renderInput={(inputParams) => <TextField {...inputParams} variant="outlined" placeholder="Select icon" />}
              fullWidth
              sx={{ minWidth: 180 }}
            />
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        flex: 0,
        sortable: false,
        renderCell: (params: GridRenderCellParams<CatalogGroupRowData>) => {
          const hasChanges = Boolean(modifiedRows[params.row.id]);
          const isSaving = createMutation.isPending || updateMutation.isPending;
          const isDeleting = deletingIds.has(params.row.id);
          const isNewRow = params.row.id.startsWith(NEW_ROW_ID_PREFIX);

          return (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Save changes">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleSave(params.row)}
                    disabled={!hasChanges || isSaving}
                    sx={{ color: hasChanges && !isSaving ? blue[600] : undefined }}
                  >
                    {isSaving ? <CircularProgress size={18} /> : <SaveIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Clear changes">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleClearChanges(params.row.id)}
                    disabled={!hasChanges || isSaving}
                  >
                    <ClearIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={isNewRow ? 'Remove row' : 'Delete group'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(params.row)}
                    disabled={isDeleting || isSaving}
                    sx={{ color: red[600] }}
                  >
                    {isDeleting ? <CircularProgress size={18} /> : <DeleteIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [
      icons,
      handleSave,
      handleClearChanges,
      handleDelete,
      createMutation.isPending,
      updateMutation.isPending,
      deletingIds,
      modifiedRows,
      type,
    ],
  );

  const handleClose = useCallback(() => {
    // Reset local state on close
    setLocalRows([]);
    setModifiedRows({});
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Manage {getGroupLabel(type, true)}</DialogTitle>
      <DialogContent>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            Error loading {getGroupLabel(type, true).toLowerCase()}:{' '}
            {error instanceof Error ? error.message : 'Unknown error'}
          </Typography>
        )}

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew} size="small">
            Add New
          </Button>
        </Stack>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Edit fields directly. Code is only editable for new rows. Click Save to persist changes.
        </Typography>

        <Box sx={{ height: 500 }}>
          <Table
            columns={columns}
            rows={allRows}
            getRowId={(row) => row.id}
            paginationMode="client"
            isLoading={isLoading}
            autoHeight={false}
            dataGridProps={{
              getRowHeight: () => 'auto',
              sx: {
                '& .MuiDataGrid-cell': {
                  py: 1,
                },
                height: '100%',
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
